import { TagSchema } from "@redotech/redo-common-model-tags-common/tag";
import { Hazmat } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import {
  ContentsType,
  NonDeliveryOption,
  OutboundShipmentTaxIdSchema,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import { CustomsDeclarationSchema } from "@redotech/redo-fulfillment-customs-common/customs-declaration";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  FulfillmentGroupDataSchema,
  FulfillmentInfoSchema,
  fulfillmentServiceSchema,
  GroupSelectedRateSchema,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OMSEventSchema } from "@redotech/redo-fulfillment-group-common/fulfillment-group-events";
import { PrintStatusSchema } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { MarketplaceFulfillmentDaySchema } from "@redotech/redo-fulfillment-group-common/marketplace-fulfillment-settings";
import { OMSUserSchema } from "@redotech/redo-fulfillment-group-common/oms-user";
import {
  ItemParcelAssignmentsSchema,
  PackingParcelsSchema,
} from "@redotech/redo-fulfillment-group-common/packing-parcels";
import {
  SelectedShippingRateSetBy,
  SelectedShippingRateSetByInfoSchema,
} from "@redotech/redo-fulfillment-group-common/selected-shipping-rate-set-by";
import { FulfillmentGroupUpdateSource } from "@redotech/redo-fulfillment-operations-common/fulfillment-group-audit";
import { WeightSchema } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundParcelSchema } from "@redotech/redo-fulfillment-parcel-common/parcel";
import {
  CanadaPostOption,
  ShipmentPaymentOptionSchema,
  SpecialRateEligibility,
} from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { DhlEcommerceLabelOptionsSchema } from "@redotech/redo-fulfillment-rate-common/outbound-shipment";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const FulfillmentGroupUpdateSchema = z.object({
  fulfillmentGroupId: z.string(),
  source: createEnum(FulfillmentGroupUpdateSource).nullish(),
  selectedParcels: z.array(OutboundParcelSchema).nullish(),
  selectedShippingRate: GroupSelectedRateSchema.nullish(),
  displayWeightGrams: z.number().nullish(),
  assignedUser: OMSUserSchema.nullish(),
  tags: z.array(TagSchema).nullish(),
  noteFromBuyer: z.string().nullish(),
  noteToBuyer: z.string().nullish(),
  internalNote: z.string().nullish(),
  printStatus: PrintStatusSchema.nullish(),
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  // @deprecated - use selectedParcels insurance instead
  includeInsurance: z.boolean().nullish(),
  // @deprecated - use selectedParcels insurance instead
  insuredAmount: z.number().nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility).nullish(),
  overrideDeliveryMethod: createEnum(RedoShopifyDeliveryMethodType).nullish(),
  scanVerified: z
    .object({
      verifiedBy: OMSUserSchema.nullish(),
      verifiedAt: z.date().nullish(),
    })
    .nullish(),
  scanVerifiedItemized: z
    .record(
      z.string(),
      z.array(
        z.object({
          verifiedBy: z.string(),
          quantityVerified: z.number(),
          serialNumbers: z.array(z.string()).nullish(),
        }),
      ),
    )
    .nullish(),
  packingSlipTemplateId: z.string().nullish(),
  newEvent: OMSEventSchema.nullish(),
  customsDeclarations: z.array(CustomsDeclarationSchema).nullish(),
  contentsType: createEnum(ContentsType).nullish(),
  contentsExplanation: z.string().nullish(),
  additionalCustomsComments: z.string().nullish(),
  nonDeliveryOption: createEnum(NonDeliveryOption).nullish(),
  uspsExemptionCode: createEnum(USPSExemptionCode).nullish(),
  itn: z.string().nullish(),
  taxIdentifierIds: z.array(z.string()).nullish(),
  customerTaxIds: z.array(OutboundShipmentTaxIdSchema).nullish(),
  ddp: z.boolean().nullish(),
  /** @deprecated */
  hazmat: createEnum(Hazmat).nullish(),
  saturdayDelivery: z.boolean().nullish(),
  assignedFulfillmentService: fulfillmentServiceSchema.nullish(),
  /** @deprecated */
  dryIceWeight: WeightSchema.nullish(),
  fulfillmentInfo: FulfillmentInfoSchema.nullish(),
  buyReturnLabel: z.boolean().nullish(),
  sendLabelPurchasedNotification: z.boolean().nullish(),
  notifyMarketplace: z.boolean().nullish(),
  // Calendar day in the merchant timezone; fulfillmentDate remains accepted for older clients.
  fulfillmentDay: MarketplaceFulfillmentDaySchema.nullish(),
  fulfillmentDate: z.date().nullish(),
  shipmentPaymentOption: ShipmentPaymentOptionSchema.nullish(),
  carbonNeutral: z.boolean().nullish(),
  canadaPostOption: createEnum(CanadaPostOption, {
    enumName: "CanadaPostOption",
  }).nullish(),
  dhlEcommerceLabelOptions: DhlEcommerceLabelOptionsSchema.nullish(),
  directDeliveryOnly: z.boolean().nullish(),
  overrideResidentialAddress: z.boolean().nullish(),
  personalizedSlipTemplateId: z.string().nullish(),
  selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
    enumName: "SelectedShippingRateSetBy",
  }).nullish(),
  selectedShippingRateSetByInfo: SelectedShippingRateSetByInfoSchema.nullish(),
  packingParcels: PackingParcelsSchema.nullish(),
  itemParcelAssignments: ItemParcelAssignmentsSchema.nullish(),
  parcelsFinalized: z.boolean().nullish(),
});

export type FulfillmentGroupUpdate = z.infer<
  typeof FulfillmentGroupUpdateSchema
>;

export interface IFulfillmentGroupUpdate extends z.ZodType<FulfillmentGroupUpdate> {}

export const updateFulfillmentGroups = {
  name: "updateFulfillmentGroups" as const,
  input: z.object({
    updates: z.array(FulfillmentGroupUpdateSchema as IFulfillmentGroupUpdate),
  }),
  output: z.record(z.string(), FulfillmentGroupDataSchema),
};
